import { Injectable, inject } from '@angular/core';
import { overrideCompletionGatesRights, TaskCompletionGateOverrideRequest } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { describeSubTasksBlockedDialog, describeTaskCompletionDialog, selectTaskCompletionDialog, TaskCompletionDialogComponent, TaskCompletionDialogContent, TaskCompletionDialogKind } from '@workbuddy/ui/blocks/task.completion.blockers';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { TaskCompletionRefusalOutcome, TaskCompletionResend } from './surface/detail/task.completion.refusal.handler';
import { readSubTaskCompletionRefusal, readTaskCompletionRefusal } from './task.completion.refusal';

/** The Web App's answer to a refused completion: dialogs A to E, and a resend only after an authorised user confirms. */
@Injectable({ providedIn: 'root' })
export class WebTaskCompletionRefusalFlow {

  private readonly dialogs = inject(WorkBuddyDialogService);
  private readonly rights = inject(RightsService);

  public async handle(error: unknown, resendWithOverride?: TaskCompletionResend): Promise<TaskCompletionRefusalOutcome> {

    const refusal = readTaskCompletionRefusal(error);
    if (!refusal) return false;

    const canOverride = !!resendWithOverride && this.rights.hasRights(...overrideCompletionGatesRights);
    const kind = selectTaskCompletionDialog(refusal, canOverride);
    const authorised = this.authorisationFor(kind);
    if (!await this.show(describeTaskCompletionDialog(kind, refusal)) || !authorised) return true;

    try {
      return { saved: await resendWithOverride(authorised) };
    } catch (retryError: unknown) {
      // The confirmed set was refused before any write; it is shown, never retried.
      const blocked = readSubTaskCompletionRefusal(retryError);
      if (blocked) {
        await this.show(describeSubTasksBlockedDialog(blocked));
        return true;
      }

      // A sub-task opened since the first answer still refuses the override; it is shown, never overridden.
      const retried = readTaskCompletionRefusal(retryError);
      if (!retried) throw retryError;
      await this.show(describeTaskCompletionDialog(selectTaskCompletionDialog(retried, false), retried));
      return true;
    }
  }

  // Dialog C completes the open sub-tasks with the parent; dialog B overrides the parent alone.
  private authorisationFor(kind: TaskCompletionDialogKind): TaskCompletionGateOverrideRequest | undefined {

    switch (kind) {
      case TaskCompletionDialogKind.CompleteWithSubTasks: return { overrideTaskCompletionGate: true, completeOpenSubTasks: true };
      case TaskCompletionDialogKind.Override: return { overrideTaskCompletionGate: true };
      default: return undefined;
    }
  }

  private async show(content: TaskCompletionDialogContent): Promise<boolean> {

    const confirmed = await this.dialogs.modal<boolean>(TaskCompletionDialogComponent, {
      width: 'min(520px, calc(100vw - 40px))',
      inputs: { content },
      aria: { label: 'Task completion' },
    });

    return confirmed === true;
  }
}
